"use client";

import { AppRoutes, NavigationConfig } from "@/config/navigation-config";
import { IconHome, IconArrowLeft, IconMoodSad } from "@tabler/icons-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

export function NotFoundContent() {
  const router = useRouter();

  // Use lazy initializer to check referrer once on mount (avoids cascading renders warning)
  const [canGoBack] = useState(() => {
    if (typeof window === "undefined") return false;
    const referrer = document.referrer;
    if (!referrer) return false;
    try {
      const referrerUrl = new URL(referrer);
      const currentUrl = new URL(window.location.href);
      return referrerUrl.origin === currentUrl.origin;
    } catch {
      return false;
    }
  });

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4">
      {/* Animated background gradient orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 h-80 w-80 animate-pulse rounded-full bg-linear-to-r from-purple-600/20 to-pink-500/20 blur-3xl" />
        <div className="absolute -right-40 -bottom-40 h-96 w-96 animate-pulse rounded-full bg-linear-to-r from-pink-500/20 to-purple-600/20 blur-3xl [animation-delay:1s]" />
        <div className="absolute top-1/3 right-1/4 h-48 w-48 animate-pulse rounded-full bg-pink-500/10 blur-3xl [animation-delay:1.5s]" />
        <div className="absolute bottom-1/3 left-1/4 h-56 w-56 animate-pulse rounded-full bg-purple-600/10 blur-3xl [animation-delay:0.5s]" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center">
        {/* Large 404 with animated gradient */}
        <div className="relative mb-6">
          {/* Glowing effect behind text */}
          <div className="absolute inset-0 flex items-center justify-center opacity-50 blur-2xl">
            <span className="text-[10rem] leading-none font-black text-pink-500 md:text-[14rem]">
              404
            </span>
          </div>
          {/* Main 404 text */}
          <h1 className="relative text-[10rem] leading-none font-black md:text-[14rem]">
            <span className="bg-linear-to-br from-pink-500 via-purple-500 to-purple-600 bg-clip-text text-transparent">
              4
            </span>
            <span className="relative inline-block">
              <span className="bg-linear-to-br from-purple-500 via-pink-500 to-pink-600 bg-clip-text text-transparent">
                0
              </span>
              {/* Sad face icon inside the zero */}
              <IconMoodSad className="absolute top-1/2 left-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 text-white/20 md:h-16 md:w-16" />
            </span>
            <span className="bg-linear-to-br from-pink-600 via-purple-500 to-purple-600 bg-clip-text text-transparent">
              4
            </span>
          </h1>
        </div>

        {/* Decorative line */}
        <div className="mb-6 h-1 w-24 rounded-full bg-linear-to-r from-pink-500 to-purple-600" />

        {/* Title */}
        <h2 className="mb-3 text-2xl font-bold tracking-tight md:text-3xl">
          Page Not Found
        </h2>

        {/* Description */}
        <p className="text-muted-foreground mb-8 max-w-md">
          We couldn&apos;t find the page you&apos;re looking for. It might have
          been moved, deleted, or perhaps it never existed. Let&apos;s get you
          back on track!
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 sm:flex-row">
          {canGoBack && (
            <button
              onClick={() => router.back()}
              className="border-border bg-background hover:bg-muted hover:text-foreground inline-flex h-12 items-center justify-center gap-2 rounded-md border px-6 py-3 text-base font-semibold shadow-xs transition-all"
            >
              <IconArrowLeft className="h-5 w-5" />
              Go Back
            </button>
          )}
          <Link
            href={NavigationConfig.getPath(AppRoutes.HOME)}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-linear-to-r from-pink-500 to-purple-600 px-6 py-3 text-base font-semibold text-white transition-all hover:from-pink-600 hover:to-purple-700"
          >
            <IconHome className="h-5 w-5" />
            Back to Home
          </Link>
        </div>

        {/* Floating particles decoration */}
        <div className="absolute -z-10">
          <div className="absolute top-20 -left-32 h-2 w-2 animate-ping rounded-full bg-pink-500" />
          <div className="absolute top-32 -right-24 h-3 w-3 animate-ping rounded-full bg-purple-500 [animation-delay:0.5s]" />
          <div className="absolute bottom-20 -left-16 h-2 w-2 animate-ping rounded-full bg-purple-600 [animation-delay:1s]" />
          <div className="absolute -right-20 bottom-10 h-2 w-2 animate-ping rounded-full bg-pink-400 [animation-delay:1.5s]" />
        </div>
      </div>

      {/* Bottom gradient line */}
      <div className="absolute right-0 bottom-0 left-0 h-px bg-linear-to-r from-transparent via-purple-500/50 to-transparent" />

      {/* Subtle grid pattern */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />
    </div>
  );
}
